import React, { useCallback, useEffect } from 'react';
import { Alert, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { getCartCount, getCartTotal } from '../../services/cartService';
import { dismissCartFeedback } from '../../store/slices/cartSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import MobileCartFeedbackPill from './MobileCartFeedbackPill';

const hiddenRoutes = new Set(['/cart', '/checkout']);

const CartSnackbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const feedback = useAppSelector((state) => state.cart.feedback);
  const cartItems = useAppSelector((state) => state.cart.items);
  const cartCount = getCartCount(cartItems);
  const cartTotal = getCartTotal(cartItems);

  const handleClose = useCallback(() => {
    dispatch(dismissCartFeedback());
  }, [dispatch]);

  useEffect(() => {
    if (!feedback?.open) return;
    if (hiddenRoutes.has(location.pathname)) {
      handleClose();
      return;
    }
    if (feedback.severity === 'success' && cartCount === 0) {
      handleClose();
    }
  }, [cartCount, feedback, handleClose, location.pathname]);

  if (!feedback?.open || hiddenRoutes.has(location.pathname)) {
    return null;
  }

  if (isMobile && feedback.severity === 'success' && cartCount > 0) {
    return (
      <MobileCartFeedbackPill
        feedbackId={feedback.id}
        open={feedback.open}
        itemCount={cartCount}
        total={cartTotal}
        onClose={handleClose}
        onOpenCart={() => {
          handleClose();
          navigate('/cart');
        }}
      />
    );
  }

  return (
    <Snackbar
      key={feedback.id}
      open={feedback.open}
      autoHideDuration={3500}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: isMobile ? 'center' : 'right',
      }}
      sx={
        isMobile
          ? { bottom: 'max(12px, env(safe-area-inset-bottom)) !important' }
          : undefined
      }
    >
      <Alert
        severity={feedback.severity}
        variant="filled"
        onClose={handleClose}
        sx={{ width: '100%' }}
      >
        {feedback.message}
      </Alert>
    </Snackbar>
  );
};

export default CartSnackbar;
